import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { devices_config } from '@devices/index';
//Helpers
import { getExtSensorsStatus } from '../../ext_sensor_helpers';
import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
import {
	real_id,
	is_gen_2,
	isEM,
	isWallDisplay,
	isPlusUni,
	isPill,
	isFanDevice,
} from '../../interface_helpers';
import { isPrevPeriodComparisonEnabled } from '../ui/chart_control_helpers';
import {
	getComponentByDevice,
	isVirtualDevice,
} from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { bthomeObjectInfos } from 'src/helpers/virtual_helpers/virtual_bthome_helper';
import { getPeripheralDeviceStatus, isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
import { isXT1Module, translateTemplateTitle } from 'src/helpers/xt1_helpers/template_helpers';
import { isPillPeripheralDevice } from 'src/helpers/thepill/thepill_data_helpers';
import { getVirtualIcon } from 'src/helpers/virtual_helpers/virtual_icons_helpers';
import { getChartIcon } from './chart_icons_helper';
//Config
import { DEFAULT_CHART_HEIGHT } from './chart_config';
import { CHART_VARIANTS_LIST } from '../ui/chart_menu_actions_helpers';
import { getSeriesAndUnitByComponent } from './device_components_config';
//Stores
import { devices_status, devices_settings } from 'src/stores';
//Types
import type { Device } from 'src/types/Device';
import type { ChartConfig, ChartConfigInterface, DeviceChartsParams } from 'src/types/Chart';

export const getDeviceChartsConfig = (device: Device): ChartConfigInterface[] => {
	const charts: ChartConfigInterface[] = [];
	let deviceChart: ChartConfigInterface = getDeviceChartsConfigByCategory(device);
	if (!deviceChart && devices_config[device.type]?.isPM && isFanDevice(device.type)) {
		deviceChart = getDeviceCharts({
			device,
			url: 'statistics/sensor/values',
			types: ['humidity', 'temperature'],
			deviceChannel: 0,
		});
	}
	if (deviceChart) charts.push(deviceChart);
	if (isFanDevice(device.type)) {
		charts.push(
			getDeviceCharts({
				device,
				url: 'v2/statistics/power-consumption',
				types: ['consumption'],
			}),
		);
	}
	const addonPeripherals = is_gen_2(device)
		? isPeripheralDevice(device.type)
			? getPeripheralDeviceStatus(device)
			: getAddonStatus(device, [], false, true)
		: getExtSensorsStatus(device);
	const isPillDevice =
		addonPeripherals.length && (isPill(device.type) || isPillPeripheralDevice(device));
	if (isPillDevice) {
		addonPeripherals.forEach((i) => {
			if (!/^(digital)/.test(i.peripheralInputType) && !/^(lora|switch)$/.test(i.peripheralType)) {
				const type = `measurement:${i.sensor.extid}:${i.sensor.component}`;
				charts.push(
					getDeviceCharts({
						device,
						url: 'v2/statistics/generic/measurement',
						types: [type],
						units: {
							[`${type}`]:
								i.sensor.xunit ||
								`${i.sensor.type == 'temperature' ? '°' : ''}${i.sensor.unit}`,
						},
						title: i.sensor.type,
						deviceChannel: i.sensor.component,
						icon: i.sensor.icon,
					}),
				);
			}
		});
		return charts;
	}
	if (addonPeripherals.length) {
		const isDHT = addonPeripherals.find((i) => i.sensor.type == 'humidity');
		if (isDHT) {
			charts.push(
				getDeviceCharts({
					device,
					url: `statistics/relay/ext_temphum_sensors`,
					types: addonPeripherals
						.filter((i) => /^(temperature|humidity)$/.test(i.sensor.type))
						.map((i) => `${i.sensor.type}::${i.sensor.extid}`),
				}),
			);
		} else if (addonPeripherals.find((i) => i.sensor.type == 'temperature')) {
			charts.push(
				getDeviceCharts({
					device,
					url: 'statistics/relay/ext_temp_sensors',
					types: addonPeripherals
						.filter((i) => i.sensor.type == 'temperature')
						.map((i) => `${i.sensor.type}:${i.sensor.extid}`),
				}),
			);
		}
		if (is_gen_2(device)) {
			const peripherals = addonPeripherals.filter(
				(i) => !/^(temperature|humidity)$/.test(i.sensor.type),
			);
			peripherals.forEach((i) => {
				if (
					!/^(digital)$/.test(i.peripheralInputType) &&
					!/^(lora|switch)$/.test(i.peripheralType)
				) {
					const url = `statistics/relay/ext_${
						i.sensor.type == 'voltmeter' ? 'voltage' : i.sensor.type
					}_sensors`;
					charts.push(
						getDeviceCharts({
							device,
							url,
							types: [`${i.sensor.type}:${i.sensor.extid}`],
							units: { xunit: i.sensor.xunit },
						}),
					);
				}
			});
		}
	}
	if (isPlusUni(device.type)) {
		const settings = devices_settings.getByRealId(device.id)?.['input:2'];
		if (settings && settings.enable !== false) {
			const xunit = settings?.xcounts?.unit || 'p';
			const freqXUnit = settings?.xfreq?.unit || 'Hz';
			charts.push(
				getDeviceCharts({
					device,
					url: 'v2/statistics/generic/accumulation',
					types: ['counter_accumulation'],
					units: { counter_accumulation: xunit },
					deviceChannel: 2,
				}),
			);
			charts.push(
				getDeviceCharts({
					device,
					url: 'v2/statistics/generic/measurement',
					types: ['freq_measurement'],
					units: { freq_measurement: freqXUnit },
					deviceChannel: 2,
				}),
			);
		}
	} else if (isXT1Module(device.type) || isVirtualDevice(device.type)) {
		const id = device.id;
		const status = devices_status.getByRealId(id);
		const settings = devices_settings.getByRealId(id);
		const deviceStatistics = getVirtualStatistics(status);
		if (settings && deviceStatistics?.length) {
			const component = getComponentByDevice(device);
			const bthomeDevice = device.type == 'bthome-device';
			deviceStatistics.forEach(
				(i: { name: string; value: string; type: string; icon?: string }) => {
					let vcSettings = settings[i.value];
					if (device.type == 'virtual-group') {
						if (!(status?.[component]?.value || []).includes(i.value)) {
							vcSettings = null;
						}
					} else if (bthomeDevice) {
						if (settings?.[component]?.addr != settings?.[i.value]?.addr) {
							vcSettings = null;
						}
					}
					if (vcSettings?.id > 0) {
						let objSetting = vcSettings;
						let channelID: number;
						let unit: string;
						if (bthomeDevice) {
							objSetting = {
								...vcSettings,
								...bthomeObjectInfos.find((i) => i.id == vcSettings?.obj_id),
							};
							channelID = vcSettings.id + 1000;
							unit = objSetting?.unit;
						} else {
							channelID = vcSettings.id + (/^object:2\d{2}$/.test(i.value) ? 3000 : 0);
							unit = vcSettings.meta?.ui?.unit;
						}
						charts.push(
							getDeviceCharts({
								device,
								url: `v2/statistics/generic/${i.type}`,
								types: [`${i.type}:${channelID}`],
								units: { [`${i.type}`]: unit || '' },
								icon: getVirtualIcon(objSetting),
								title: objSetting?.name || i.value,
								deviceChannel: channelID,
							}),
						);
					}
				},
			);
		}
	} else if (device.category == 'pv') {
		const url = 'v2/statistics/power-consumption';
		charts.push(
			getDeviceCharts({
				device,
				url,
				types: ['consumption'],
				title: 'Home',
				deviceChannel: 0,
			}),
		);
		charts.push(
			getDeviceCharts({
				device,
				url,
				types: ['consumption'],
				title: 'Battery',
				deviceChannel: 1,
			}),
		);
		charts.push(
			getDeviceCharts({
				device,
				url,
				types: ['consumption'],
				title: 'Solar Panel',
				deviceChannel: 2,
			}),
		);
	}
	return charts;
};

const getDeviceChartsConfigByCategory = (device: Device): ChartConfigInterface => {
	let deviceChart: ChartConfigInterface;
	switch (device.category) {
		case 'light':
		case 'roller':
		case 'relay':
			if (devices_config[device.type]?.isPM) {
				deviceChart = getDeviceCharts({
					device,
					url: 'v2/statistics/power-consumption',
					types:
						(is_gen_2(device) || device.type == 'SHSW-25') && device.category != 'roller'
							? ['consumption', 'voltage']
							: ['consumption'],
				});
			}
			if (isWallDisplay(device.type)) {
				//wall display
				deviceChart = getDeviceCharts({
					device,
					url: 'statistics/sensor/values',
					types: ['temperature', 'humidity'],
				});
			}
			break;
		case 'sensor':
			switch (device.type) {
				case 'SHHT-1':
				case 'SNSN-0013A':
				case 'S3SN-0U12A': //h&t g3
				case 'S3SN-1U12A': //h&t g3 ffs
				case 'SHSEN-1':
				case 'SBHT-003C':
				case 'SBHT-103C':
				case 'SBHT-203C':
					deviceChart = getDeviceCharts({
						device,
						url: 'statistics/sensor/values',
						types: ['temperature', 'humidity'],
					});
					break;
				case 'SHWT-1':
				case 'SHSM-01':
				case 'SHDW-2':
				case 'SHMOS-02':
					deviceChart = getDeviceCharts({
						device,
						url: 'statistics/sensor/values',
						types: ['temperature'],
					});
					break;
				case 'SHGS-1':
					deviceChart = getDeviceCharts({
						device,
						url: 'statistics/sensor/concentration',
						types: ['ppm'],
					});
					break;
				case 'SBDI-003E':
					deviceChart = getDeviceCharts({
						device,
						url: 'v2/statistics/generic/measurement',
						types: ['distance'],
						deviceChannel: 1,
					});
					break;
				case 'SBWS-90CM': //Weather Station
					deviceChart = getDeviceCharts({
						device,
						url: 'v2/statistics/weather-station',
						types: [
							'temperature',
							'humidity',
							'pressure',
							'precipitation',
							'illuminance',
							'uv',
							'wind_speed',
						],
					});
					break;
				case 'SBMS-001A': //Blu Soil Sensor
					deviceChart = {
						url: 'v2/statistics/generic/measurement',
						charts: [
							...getDeviceCharts({
								device,
								url: 'v2/statistics/generic/measurement',
								types: ['measurement:moisture:0'],
								units: { measurement: '%' },
								title: 'moisture',
								deviceChannel: 'moisture:0',
							}).charts,
							...getDeviceCharts({
								device,
								url: 'v2/statistics/generic/measurement',
								types: ['measurement:temperature:0'],
								units: { measurement: '°C' },
								title: 'temperature',
								deviceChannel: 'temperature:0',
							}).charts,
						],
					};
					break;
			}
			break;
		case 'thermostat':
			if (isWallDisplay(device.type)) {
				//Wall display has new ability to extract thermostat component from the device
				deviceChart = getDeviceCharts({
					device,
					url: 'statistics/sensor/values',
					types: ['temperature', 'humidity'],
					deviceChannel: device.channel,
				});
			} else if (!isXT1Module(device.type)) {
				deviceChart = getDeviceCharts({
					device,
					url: 'statistics/sensor/values',
					types: ['temperature', 'valve_position'],
					deviceChannel: device.channel,
				});
			}
			break;
		case 'emeter':
			const splitPhases = isEM(device.type) || device.mode === 'monophase';
			// console.log('splitPhases', splitPhases)
			deviceChart = getDeviceCharts({
				device,
				url: `v2/statistics/power-consumption/em-${splitPhases ? '1p' : '3p'}`,
				types: ['consumption', 'voltage'],
			});
			break;
		case 'power_meter':
			deviceChart = getDeviceCharts({
				device,
				url: 'v2/statistics/power-consumption',
				types: ['consumption', 'voltage'],
			});
			break;
	}

	return deviceChart;
};

const getDeviceCharts = (params: DeviceChartsParams): ChartConfigInterface => {
	const { device, url, types, units = {}, title = null, deviceChannel = null, icon } = params;
	const charts: ChartConfig[] = [];
	const prevPeriodComparison = isPrevPeriodComparisonEnabled() !== false;

	types.forEach((rawType) => {
		const typeSplit = rawType.split(':');
		const type = typeSplit[0];
		const extid = typeSplit[1]
			? `_${typeSplit[1]}`
			: device.category == 'pv'
				? `_${deviceChannel || 0}`
				: '';
		const unitOverride = units?.[rawType] ?? units?.[type] ?? units?.[`${type}${extid}`];
		const unitsForHelper = unitOverride ? { ...units, [type]: unitOverride } : units;

		const seriesData = getSeriesAndUnitByComponent(
			device,
			type,
			extid,
			unitsForHelper,
			prevPeriodComparison,
		);

		const chart: ChartConfig = {
			id: `${type}${extid}`,
			url,
			name: type,
			metric: getSemanticChartMetric(type, title, seriesData.unit),
			extid: extid.substring(1),
			sensorID: typeSplit[2] ? typeSplit[2] : extid.substring(1),
			type: CHART_VARIANTS_LIST[device?.[`${type}${extid}_type`]] || 'sparkline',
			series: seriesData.series,
			units: seriesData.unit,
			deviceID: real_id(device.id),
			deviceType: device.type,
			deviceCategory: device.category,
			height: DEFAULT_CHART_HEIGHT,
			icon: icon || getChartIcon(type),
			chartTitle: get(format)(
				`collapse_title_chart_${(title || type).toLowerCase().replace(/\s+/g, '_')}`,
				{
					default: translateTemplateTitle(title || type),
				},
			),
			title,
			deviceChannel,
		};
		charts.push(chart);
	});
	return { url, charts };
};

const getSemanticChartMetric = (type: string, title: string | null, unit: string): string => {
	const normalizedTitle = title?.trim().toLowerCase().replace(/\s+/g, '_');
	const knownTitleMetrics = new Set([
		'temperature',
		'humidity',
		'moisture',
		'pressure',
		'precipitation',
		'illuminance',
		'uv',
		'wind_speed',
		'distance',
		'voltage',
		'power',
		'consumption',
		'ppm',
		'valve_position',
		'battery',
	]);

	if (normalizedTitle && knownTitleMetrics.has(normalizedTitle)) {
		return normalizedTitle;
	}
	if (type === 'voltmeter') {
		return 'voltage';
	}
	if (/^°[CF]$/.test(unit)) {
		return 'temperature';
	}
	if (type === 'freq_measurement' || unit === 'Hz') {
		return 'frequency';
	}
	return type;
};

const getVirtualStatistics = (status) => {
	const deviceMeasurements = (status?._measurements || []).map((i) => {
		return { ...i, type: 'measurement' };
	});
	const deviceAccumulations = (status?._accumulations || []).map((i) => {
		return { ...i, type: 'accumulation' };
	});
	const deviceCounters = (status?._counters || []).map((i) => {
		return { ...i, type: 'counter' };
	});
	return [...deviceMeasurements, ...deviceAccumulations, ...deviceCounters];
};
